import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Params, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { INavigationEnvironment } from 'projects/shared/lib/interfaces/environment/navigation/navigation-environment.interface';
import { ISocialMediaEntryEnvironment } from 'projects/shared/lib/interfaces/environment/social/social-entry-environment.interface';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ECustomTextType } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { IAuthService, IAuthServiceToken } from 'projects/shared/lib/services/infrastructure/authentication/auth-service.interface';
import { IUserService, IUserServiceToken } from 'projects/shared/lib/services/data/user/user-service.interface';
import { IIdpApiService, IIdpApiServiceToken } from 'projects/shared/lib/services/api/idp/idp-api-service.interface';
import { IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';
import { CommonModule } from '@angular/common';
import { SocialIconsComponent } from '../social-icons/social-icons.component';
import { IExternalLegalLinks } from 'projects/shared/lib/interfaces/environment/footer/footer-environment.interface';

@Component({
  selector: 'app-footer',
  imports: [
    CommonModule,
    RouterModule,
    SocialIconsComponent
  ],
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss']
})
export class FooterComponent implements OnInit, OnDestroy {
  public brand;
  public eCustomTextType: typeof ECustomTextType;
  public isAuthenticated: boolean;
  public footerMenuRegisterLink: boolean;
  private authSubscription: Subscription | undefined;
  protected anonymousLandingPage: INavigationEnvironment;
  public socialMediaList: ISocialMediaEntryEnvironment[] | undefined;
  public currentYear: string | undefined;

  public externalContactLink?: string;
  public footerMenuContactRequestLink: boolean;
  public footerMenuShippingLink: boolean;
  public externalLegalLinks?: IExternalLegalLinks;

  protected logger: LoggerService;
  protected authService: IAuthService;
  protected readonly userService: IUserService;
  protected readonly toastService: IToastService = inject(IToastServiceToken);
  protected router: Router;
  protected readonly idpApiService: IIdpApiService | null;

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.authService = inject(IAuthServiceToken);
    this.userService = inject(IUserServiceToken);
    this.router = ServiceLocator.injector.get(Router);
    this.idpApiService = inject(IIdpApiServiceToken, { optional: true });

    this.externalContactLink = ENVIRONMENT.footer?.externalContactLink;
    this.footerMenuContactRequestLink = ENVIRONMENT.footer?.footerMenuContactRequestLink ?? true;
    this.footerMenuShippingLink = ENVIRONMENT.footer?.footerMenuShippingLink ?? false;
    this.externalLegalLinks = ENVIRONMENT.footer?.externalLegalLinks;

    this.brand = $localize`:@@NavTopMenuBrand:INTEGRA-Shop`;

    this.eCustomTextType = ECustomTextType;
    this.isAuthenticated = false;
    this.footerMenuRegisterLink = ENVIRONMENT.footer?.footerMenuRegisterLink ?? true;
    this.anonymousLandingPage = ENVIRONMENT.anonymousLandingPage;
    ServiceLocator.injector.get(IConfigurationServiceToken).anonymousLandingPageGet().subscribe((anonymousLandingPage) => {
      this.anonymousLandingPage.routerlink = anonymousLandingPage.routerlink as string;
      let queryParams = new URLSearchParams(anonymousLandingPage.queryParams);
      let queryParamsObject: Params = {};
      queryParams.forEach((value, key) => {
        queryParamsObject[key] = value;
      });
      this.anonymousLandingPage.queryParams = queryParamsObject;
    });

    this.socialMediaList = ENVIRONMENT.social?.entries;
  }

  public ngOnInit(): void {
    if (!this.authSubscription) {
      this.authSubscription = this.authService.authenticatedGet().subscribe({
        next: (isAuthenticated: boolean) => {
          this.isAuthenticated = isAuthenticated;
        }
      });
    }
    this.getCurrentYear();
  }

  public ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  public logout() {
    this.idpApiService?.logout();

    this.userService.userLogout().subscribe({
      next: () => {
        this.authService.accessJWTClear();

        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Success,
          title: $localize`:@@NavFooterMenuLogoutSuccessToastTitle:Logout successful`,
        }));

        this.router.navigate([this.anonymousLandingPage.routerlink], { queryParams: this.anonymousLandingPage.queryParams });
      },
      error: (error) => {
        this.logger.error(error, this.constructor.name);
        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Danger,
          title: $localize`:@@NavFooterMenuLogoutErrorToastTitle:Logout failed`,
          code: JSON.parse(error.response).errorCode
        }));
      },
      complete: () => { }
    });
  }

  private getCurrentYear() {
    let currentDate = new Date();
    this.currentYear = currentDate.getFullYear().toString();
  }
}